import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { IndustrySelect } from "./IndustrySelect";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Building2 } from "lucide-react";
import { ENTITY_CATEGORIES } from "../../lib/onboardingConfig";

const ENTITY_TYPES = ["Private Limited Company", "Public Limited Company", "Limited Liability Partnership", "Partnership", "Sole Proprietorship", "Society / Non-Profit", "Branch of Foreign Company"];

const fmt = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  return digits ? Number(digits).toLocaleString("en-SG") : "";
};

function CurrencyInput({ value, onChange, testid, placeholder }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground font-mono">S$</span>
      <Input inputMode="numeric" className="pl-8 font-mono" value={fmt(value)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

export function OrgDetailsStage({ sessionId, verifiedEmail, onBack, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name: "", uen: "", entity_type: "Private Limited Company", entity_category: "SME",
    industry_code: "", employees: "", revenue_sgd: "", assets_sgd: "",
    cpa_taxable: false, eca_registered: false, billing_contact: "", fy_end_month: 12,
    street: "", building: "", unit: "", city: "Singapore", country: "Singapore", postal: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/email/organisation", {
        session_id: sessionId,
        company_name: form.company_name.trim(),
        uen: form.uen.trim(),
        entity_type: form.entity_type,
        entity_category: form.entity_category,
        industry_code: form.industry_code,
        employees: form.employees ? parseInt(form.employees) : null,
        revenue_sgd: form.revenue_sgd ? parseFloat(form.revenue_sgd) : null,
        assets_sgd: form.assets_sgd ? parseFloat(form.assets_sgd) : null,
        cpa_taxable: form.cpa_taxable,
        eca_registered: form.eca_registered,
        fy_end_month: parseInt(form.fy_end_month),
        billing_contact: form.billing_contact || null,
        address_street: form.street || null,
        address_building: form.building || null,
        address_unit: form.unit || null,
        address_city: form.city || "Singapore",
        address_country: form.country || "Singapore",
        address_postal: form.postal || null,
      });
      onSaved(data);
    } catch (e) { toast.error(apiErr(e, "Could not save organisation details")); }
    finally { setBusy(false); }
  };

  const addressOk = form.street.trim() && form.postal.trim();
  const valid = form.company_name.trim().length >= 2 && form.uen.trim().length >= 4 && form.industry_code && addressOk;

  return (
    <div className="max-w-2xl" data-testid="org-details-stage">
      <h2 className="font-display text-3xl font-bold tracking-tight">Tell us about your organisation</h2>
      <p className="text-muted-foreground text-sm mt-2 max-w-lg">
        Verified as <span className="text-foreground font-medium">{verifiedEmail}</span>. Confirm your organisation
        details — these drive your regulatory tier, materiality and setup.
      </p>

      <div className="card-flat bg-surface p-5 mt-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Registered company name *</Label>
            <Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="e.g. Verdant Facilities Pte Ltd" data-testid="org-company-input" />
          </div>
          <div>
            <Label>UEN / Business Registration Number *</Label>
            <Input value={form.uen} onChange={(e) => set("uen", e.target.value.toUpperCase())} placeholder="e.g. 201812345K" data-testid="org-uen-input" />
          </div>
          <div>
            <Label>Entity type</Label>
            <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
              <SelectTrigger data-testid="org-entity-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Entity category</Label>
            <Select value={form.entity_category} onValueChange={(v) => set("entity_category", v)}>
              <SelectTrigger data-testid="org-category"><SelectValue /></SelectTrigger>
              <SelectContent>{ENTITY_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Industry (SASB) *</Label>
            <IndustrySelect value={form.industry_code} onChange={(v) => set("industry_code", v)} testId="org-industry" />
          </div>
          <div>
            <Label>Number of employees</Label>
            <Input type="number" value={form.employees} onChange={(e) => set("employees", e.target.value)} data-testid="org-employees" />
          </div>
          <div>
            <Label>Annual revenue (SGD)</Label>
            <CurrencyInput value={form.revenue_sgd} onChange={(v) => set("revenue_sgd", v)} testid="org-revenue" placeholder="e.g. 8,500,000" />
          </div>
          <div>
            <Label>Total assets (SGD)</Label>
            <CurrencyInput value={form.assets_sgd} onChange={(v) => set("assets_sgd", v)} testid="org-assets" placeholder="e.g. 3,200,000" />
          </div>
        </div>

        {/* Registered address */}
        <div className="border-t border-border pt-4">
          <div className="label-eyebrow mb-3">Registered address *</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Street *</Label>
              <Input value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="e.g. 10 Anson Road" data-testid="org-addr-street" />
            </div>
            <div>
              <Label>Building (optional)</Label>
              <Input value={form.building} onChange={(e) => set("building", e.target.value)} placeholder="e.g. International Plaza" data-testid="org-addr-building" />
            </div>
            <div>
              <Label>Unit (optional)</Label>
              <Input value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="e.g. 20-01" data-testid="org-addr-unit" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="org-addr-city" />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="org-addr-country" />
            </div>
            <div>
              <Label>Postal code *</Label>
              <Input value={form.postal} onChange={(e) => set("postal", e.target.value)} placeholder="e.g. 079903" data-testid="org-addr-postal" />
            </div>
          </div>
        </div>

        {/* Regulatory exposure */}
        <div className="grid sm:grid-cols-2 gap-3 pt-1">
          <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-surface-hover" data-testid="org-cpa">
            <input type="checkbox" checked={form.cpa_taxable} onChange={(e) => set("cpa_taxable", e.target.checked)} className="mt-0.5 accent-[hsl(var(--accent))]" />
            <div>
              <div className="text-sm font-medium">Carbon Pricing Act exposure</div>
              <div className="text-xs text-muted-foreground">Annual emissions ≥ 25k tCO₂e</div>
            </div>
          </label>
          <label className="flex items-start gap-3 p-3 border border-border rounded-sm cursor-pointer hover:bg-surface-hover" data-testid="org-eca">
            <input type="checkbox" checked={form.eca_registered} onChange={(e) => set("eca_registered", e.target.checked)} className="mt-0.5 accent-[hsl(var(--accent))]" />
            <div>
              <div className="text-sm font-medium">Energy Conservation Act exposure</div>
              <div className="text-xs text-muted-foreground">Registered energy user</div>
            </div>
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-7">
        <Button variant="outline" onClick={onBack} data-testid="org-back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Button variant="primary" size="lg" className="gap-2" disabled={busy || !valid} onClick={submit} data-testid="org-continue-btn">
          <Building2 className="h-4 w-4" /> {busy ? "Saving…" : "Save & continue"} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
